import React, { Fragment, useState, useCallback, useEffect, FC } from 'react';
import { TextField, Switch, FormControlLabel, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
// import { AddFilterAction } from '~/components';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { Account } from '~/types';

const resource = 'accounts';

export type AccountListFilters = {
  number?: string;
  type?: string;
  currency?: string;
  status?: Account['status'];
  withDeleted?: boolean;
  withError?: boolean;
};

interface Props {
  cleanFilters?: boolean;
}

const label = (key: string) => `resources.accounts.fields.${key}`;

const Filters: FC<Props> = (props) => {
  const { cleanFilters = true } = props;
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string) => translate(label(key));
  const listFilterClasses = useListFilterStyles();
  const classes = useStyles();

  const filters = useSelector(selectors.getFilters<AccountListFilters>(resource));
  const [localValues, setLocalValues] = useState<AccountListFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>(
    Object.keys(filters).filter((k) => !!(filters as any)[k])
  );

  const [isInitialized, setInitialized] = useState<boolean>(false);

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (!isInitialized) {
      if (cleanFilters || filtersAreEmpty) {
        dispatch(cleanupFilters({ resource }));
        setAddedFilters([]);
      }
      setInitialized(true);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty, isInitialized]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: any) => {
    dispatch(filterChange({ resource, filters: { [name]: value ?? null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (isInitialized) {
      setLocalValues((prev) => ({ ...prev, [name]: value }));
      debouncedDispatchFilterChange(name, value);
    }
  };

  //   const handleSelectInput = (e: any) => {
  //     const { name, value } = e.target;
  //     setLocalValues((prev) => ({ ...prev, [name]: value }));
  //     dispatchFilterChange(name, value);
  //   };

  const hideFilter = (filter: string) => {
    dispatchFilterChange(filter, null);
    setAddedFilters((prev) => prev.filter((f) => f !== filter));
  };

  //   const allOptionalFilters = ['type', 'currency', 'status'];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      //   case 'type':
      //     return (
      //       <SelectInput<string>
      //         label={t('type')}
      //         name='type'
      //         value={localValues.type || ''}
      //         options={[
      //           { value: 'REGULAR', text: 'REGULAR' },
      //           { value: 'META', text: 'META' },
      //           { value: 'SETTLEMENT', text: 'SETTLEMENT' },
      //         ]}
      //         onChange={handleSelectInput}
      //         allowEmpty
      //         className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       />
      //     );
      //   case 'currency':
      //     return (
      //       <TextField
      //         label={t('currency')}
      //         name='currency'
      //         value={localValues.currency || ''}
      //         onChange={handleTextInput}
      //         className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       />
      //     );
      //   case 'status':
      //     return (
      //       <SelectInput<string>
      //         label={t('status')}
      //         name='status'
      //         value={localValues.status || ''}
      //         options={[
      //           { value: 'ACTIVE', text: 'ACTIVE' },
      //           { value: 'BLOCKED', text: 'BLOCKED' },
      //           { value: 'CLOSED', text: 'CLOSED' },
      //         ]}
      //         onChange={handleSelectInput}
      //         allowEmpty
      //         className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
      //       />
      //     );
      default:
        return null;
    }
  };

  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (!component) return null;
      return (
        <div className={listFilterClasses.flexContainer} key={filter}>
          {component}
          <IconButton
            aria-label='delete'
            className={listFilterClasses.deleteIcon}
            onClick={() => hideFilter(filter)}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      );
    });

  return (
    <Fragment>
      <div className={listFilterClasses.container}>
        <div className={classes.leftSide}>
          <div className={listFilterClasses.inputsGroup}>
            <TextField
              label={t('number')}
              name='number'
              value={localValues.number || ''}
              onChange={handleTextInput}
              className={listFilterClasses.formControl}
            />
            {renderAddedFilters()}
            <FormControlLabel
              control={
                <Switch
                  checked={!!localValues.withDeleted}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setLocalValues((prev) => ({ ...prev, withDeleted: val }));
                    dispatchFilterChange('withDeleted', val || null);
                  }}
                />
              }
              label={t('withDeleted')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!localValues.withError}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setLocalValues((prev) => ({ ...prev, withError: val }));
                    dispatchFilterChange('withError', val || null);
                  }}
                />
              }
              label={t('withError')}
            />
          </div>
        </div>
        <div className={classes.rightSide}>
          <BackgroundTaskExport type='ACCOUNT_EXPORT' queryArguments={handleFilters(localValues)} />
        </div>
      </div>
    </Fragment>
  );
};

const handleFilters = (filters: AccountListFilters) => {
  if (!filters) return {};
  return filters;
};

const useStyles = makeStyles(() => ({
  leftSide: {
    maxWidth: 'calc(100% - 150px)',
  },
  rightSide: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: '8px',
  },
}));

export default Filters;
